import React, { memo, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { ToastContainer, Slide } from "react-toastify";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { store } from "./redux/store";
import RouteList from "./RouteList";
import "./libcss";
import Loader from "./component/Common/Loader";
import ErrorBoundary from "./component/ErrorBoundary";
import { SurveyModal } from "./component/Survey";
import { SurveyProvider } from "./container/context/SurveyContext";

const envMode = process.env.REACT_APP_ENV;

const shouldEnableSentry = ["prod", "test", "dev"].includes(envMode);

let sentryInitialized = false;
let lastRestartAt = 0;

function restartSentrySession(reason) {
  if (!sentryInitialized) return;

  const now = Date.now();

  // Prevent restart spam (5s debounce)
  if (now - lastRestartAt < 5000) return;
  lastRestartAt = now;

  try {
    Sentry.endSession();
    Sentry.startSession();

    Sentry.setContext("session_restart", {
      reason: reason,
      timestamp: new Date().toISOString(),
    });

    Sentry.flush(5000);
    console.log("[Sentry] Session restarted:", reason);
  } catch (e) {
    console.warn("[Sentry] Failed to restart session", e);
  }
}

if (shouldEnableSentry) {
  Sentry.init({
    dsn: "https://bc8d3849e6d69c71a0a2d6fb8c5a4294@o4506863228616704.ingest.us.sentry.io/4511568628482048",

    release: process.env.REACT_APP_VERSION,
    environment: envMode,

    autoSessionTracking: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [
          /^https:\/\/dev\.api\.incruiter\.com/,
          /^https:\/\/test\.api\.incruiter\.com/,
          /^https:\/\/api\.incruiter\.com/,
          /^https:\/\/.*\.incruiter\.com/,
        ],
        networkRequestHeaders: ["Authorization", "Content-Type"],
        networkResponseHeaders: ["Content-Type"],
      }),
      Sentry.replayCanvasIntegration(),
    ],

    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,

    tracePropagationTargets: ["localhost", /^https:\/\/.*\.incruiter\.com/],
    enableLogs: true,
  });

  sentryInitialized = true;
  Sentry.setTag("env", envMode);

  window.addEventListener("online", function () {
    restartSentrySession("network_online");
  });

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") {
      restartSentrySession("tab_visible");
    }
  });
}

const CloseButton = ({ closeToast }) => (
  <i
    className="fa fa-times-circle align-self-center f-18 text-white"
    onClick={closeToast}
  />
);

const MainApp = memo(() => {
  return (
    <Router>
      <Provider store={store}>
        <ToastContainer
          autoClose={2000}
          draggable={false}
          transition={Slide}
          closeButton={CloseButton}
          hideProgressBar={true}
          toastClassName="toast-notification"
        />
        <ErrorBoundary>
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route path="/*" element={<RouteList />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Provider>
    </Router>
  );
});
const rootElement = document.getElementById("root");
const root = createRoot(rootElement);
root.render(<MainApp />);
